import React, { useMemo, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import {
  BootstrapSwitchButton,
  CellYesNoV8,
  CellYesNoEngV8,
  TablePaginatedV8,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { FaUndo } from 'react-icons/fa';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';

export type BulkFinalValueMode = 'yes' | 'no' | 'default';

type FinalValueHeaderProps = {
  canEdit: boolean;
  onBulkFinalValue?: (mode: BulkFinalValueMode) => void;
  title: string;
  t: (key: string) => string;
};

/** Header controls must stop propagation — TablePaginatedV8 wraps headers with sort onClick. */
const FinalValueColumnHeader: React.FC<FinalValueHeaderProps> = ({
  canEdit,
  onBulkFinalValue,
  title,
  t,
}) => {
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const run =
    (mode: BulkFinalValueMode) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onBulkFinalValue?.(mode);
    };

  return (
    <div className="text-center user-select-auto" onClick={stop} onMouseDown={stop}>
      <span className="small d-block">{title}</span>
      {canEdit && onBulkFinalValue ? (
        <div className="btn-group btn-group-sm mt-1 d-flex justify-content-center" role="group">
          <button
            type="button"
            className="btn btn-success py-0 px-2 text-white fw-semibold border border-white"
            style={{ fontSize: '0.7rem', lineHeight: 1.2 }}
            title={t('Όλα → ΝΑΙ')}
            onClick={run('yes')}
          >
            {t('ΝΑΙ')}
          </button>
          <button
            type="button"
            className="btn btn-danger py-0 px-2 text-white fw-semibold border border-white"
            style={{ fontSize: '0.7rem', lineHeight: 1.2 }}
            title={t('Όλα → ΟΧΙ')}
            onClick={run('no')}
          >
            {t('ΟΧΙ')}
          </button>
          <button
            type="button"
            className="btn btn-primary py-0 px-1 text-white fw-semibold border border-white"
            style={{ fontSize: '0.7rem', lineHeight: 1.2 }}
            title={t('Επαναφορά προεπιλογών')}
            onClick={run('default')}
          >
            <FaUndo size={11} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
};

/* ----------- User Params Grid (resolved view) ------------------ */
export const DataGrid = forwardRef((props: any, ref) => {
  const { data, loading, canEdit, onSaveRow, onBulkFinalValue } = props;

  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const handleSaveRow = useCallback(
    async (rowIndex: number, row: any, changes: any) => {
      if (!canEdit) return false;
      return onSaveRow && onSaveRow(row, changes);
    },
    [canEdit, onSaveRow],
  );

  const columns = useMemo(
    () => [
      {
        id: 'paramGroup',
        header: t('Ομάδα Παραμέτρων'),
        columnName: t('Ομάδα Παραμέτρων'),
        accessorKey: 'paramGroup',
        cell: ({ row }: any) => (
          <div>
            <div>{row.original.paramGroup || <span className="text-muted">-</span>}</div>
            <div className="text-muted">
              {row.original.paramName || <span className="text-muted">-</span>}
            </div>
          </div>
        ),
        size: 15,
      },
      {
        id: 'description',
        header: t('Περιγραφή'),
        columnName: t('Περιγραφή'),
        accessorKey: 'description',
        cell: ({ row }: any) => (
          <div>
            <div>{row.original.description || <span className="text-muted">-</span>}</div>
            {row.original.notes ? (
              <small className="text-muted d-block mt-1">{row.original.notes}</small>
            ) : null}
          </div>
        ),
        size: 30,
      },
      {
        id: 'defaultValue',
        header: t('Default Τιμή'),
        columnName: t('Default Τιμή'),
        accessorKey: 'defaultValue',
        cell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        size: 8,
      },
      {
        id: 'finalValue',
        enableSorting: false,
        header: () => (
          <FinalValueColumnHeader
            canEdit={canEdit}
            onBulkFinalValue={onBulkFinalValue}
            title={t('Τιμή Χρήστη')}
            t={t}
          />
        ),
        columnName: t('Τιμή Χρήστη'),
        accessorKey: 'finalValue',
        cell: (cellProps: any) => {
          const { row } = cellProps;
          const YesNoCell = i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8;
          if (!canEdit) {
            return <YesNoCell onlabel={t('ΝΑΙ')} offlabel={t('ΟΧΙ')} {...cellProps} />;
          }
          return (
            <div
              className="text-center"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BootstrapSwitchButton
                checked={row.original.finalValue || false}
                size="xs"
                width={90}
                onlabel={t('ΝΑΙ')}
                offlabel={t('ΟΧΙ')}
                onChange={async () => {
                  const newValue = !row.original.finalValue;
                  await handleSaveRow(row.index, row.original, { finalValue: newValue });
                }}
                onstyle="success"
                offstyle="danger"
              />
            </div>
          );
        },
        size: 8,
      },
    ],
    [t, i18n.language, canEdit, handleSaveRow, onBulkFinalValue],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="user-params-grid"
          appStore={appStore}
          key="user-params-grid"
          columns={columns as any}
          data={data}
          dataCount={data.length}
          fixedPageSize={data.length > 0 ? data.length : undefined}
          hidePagination
          showGridFooter={false}
          onSaveRow={handleSaveRow}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '120px' }} />
      )}
    </LoadingOverlay>
  );
});

DataGrid.displayName = 'UserParamsDataGrid';
